import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { QrCode, X, Copy, MessageCircleMore, Gift } from "lucide-react";
import axios from "axios";
import { getUser } from "@/lib/user";

const API = process.env.REACT_APP_BACKEND_URL;

/** Partage d'une annonce entre amis : QR code + lien copiable + WhatsApp. */
export const PartageAnnonce = ({ annonceId, titre, reference = null }) => {
  const [ouvert, setOuvert] = useState(false);
  const [parrainages, setParrainages] = useState(null);
  const user = getUser();
  // Le lien pointe vers l'aperçu servi par le serveur : WhatsApp et Facebook y trouvent
  // le titre, le texte et la photo (les robots des messageries ne lisent pas le
  // JavaScript). L'internaute est ensuite redirigé vers l'annonce. Le parrain reste
  // transmis : 24 h offertes si une inscription en découle.
  const lien = `${API}/api/partage/${reference || annonceId}${user ? `?parrain=${user.id}` : ""}`;
  const texte = `${titre || "Cette annonce"} sur FodindinCa : ${lien}`;

  useEffect(() => {
    if (!ouvert || !user) return;
    axios
      .get(`${API}/api/parrainages/${user.id}`)
      .then((r) => setParrainages(r.data))
      .catch(() => {});
  }, [ouvert, user?.id]);

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(lien);
      toast.success("Lien copie", { description: "Collez-le dans WhatsApp ou un SMS." });
    } catch {
      toast.error("Copie impossible sur cet appareil.");
    }
  };

  return (
    <>
      <button
        onClick={() => setOuvert(true)}
        data-testid={`partager-annonce-${annonceId}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD5] px-3 py-1.5 text-xs font-bold text-stone-700 transition-colors hover:border-stone-900"
      >
        <QrCode size={14} /> Partager
      </button>

      <AnimatePresence>
        {ouvert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOuvert(false)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
            data-testid="partage-overlay"
          >
            <motion.div
              initial={{ scale: 0.94, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-3xl bg-white p-7 text-center"
              data-testid="partage-modal"
            >
              <div className="flex items-start justify-between">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">
                  Partager cette annonce
                </p>
                <button onClick={() => setOuvert(false)} data-testid="partage-fermer">
                  <X size={18} className="text-stone-400" />
                </button>
              </div>

              <img
                src={`${API}/api/annonces/${annonceId}/qrcode${user ? `?parrain_id=${user.id}` : ""}`}
                alt="QR code de l'annonce"
                data-testid="partage-qrcode"
                className="mx-auto mt-5 h-52 w-52 rounded-2xl border border-[#E5DFD5] p-2"
              />
              <p className="mt-4 text-sm text-stone-600">
                Faites scanner ce code : votre ami arrive directement sur l'annonce.
              </p>

              {user && (
                <div
                  className="mt-4 rounded-2xl border border-[#0A5C36]/25 bg-[#0A5C36]/[0.06] px-4 py-3 text-left"
                  data-testid="partage-parrainage"
                >
                  <p className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A5C36]">
                    <Gift size={13} /> 24 h d'accès offertes
                  </p>
                  <p className="mt-1 text-xs text-stone-600">
                    Dès qu'un ami s'inscrit grâce à ce lien, 24 h d'accès à cette rubrique vous sont
                    offertes (jusqu'à {parrainages?.plafond_quotidien || 3} par jour).
                    {parrainages && (
                      <>
                        {" "}
                        <b className="text-stone-900" data-testid="partage-parrainage-compteur">
                          {parrainages.total} ami(s) parrainé(s)
                        </b>
                        .
                      </>
                    )}
                  </p>
                </div>
              )}

              <button
                onClick={copier}
                data-testid="partage-copier"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#12100E] py-3 text-sm font-bold text-white"
              >
                <Copy size={15} /> Copier le lien
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(texte)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="partage-whatsapp"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 text-sm font-bold text-white"
              >
                <MessageCircleMore size={15} /> Envoyer sur WhatsApp
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
